import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { ArrowUpDown } from 'lucide-react'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import {
  resolveDisplaySelection,
  resolveEventParticipantCharacter,
} from './eventActivityParticipants'
import capitalize from 'lodash/capitalize'
import EventParticipantContextMenu from './EventParticipantContextMenu'
import EventParticipantStatusItems from './EventParticipantStatusItems'
import EventParticipantCommentIndicators from './EventParticipantCommentIndicators'
import { cn } from '@/lib/utils'
import { useTeamEventPlanningInfoContext } from '../context/TeamEventPlanningInfoContext'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import EventActivityParticipantActivityPlanningInfo from './EventActivityParticipantActivityPlanningInfo'

type Props = {
  entry: EventActivityParticipantEntry
  activity: wowaudit.EventActivity
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  canAdminEdit: boolean
  variant: 'selected' | 'benched' | 'unavailable'
  applyToAllActivities?: boolean
  hideStatus?: boolean
  hideEventActions?: boolean
}

const EventActivitySetupParticipant = ({
  entry,
  activity,
  selectableStatuses,
  canAdminEdit,
  variant,
  applyToAllActivities,
  hideStatus,
  hideEventActions,
}: Props) => {
  const activityId = activity.id
  const { updateSelection, updateStatus } = useTeamEventUpdateContext()
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const { showPlanningInfo } = useTeamEventPlanningInfoContext()
  const { event } = useTeamEventParticipantsContext()
  const {
    data: { user },
  } = usePageInfo()
  const selection = resolveDisplaySelection(
    entry,
    activityId,
    applyToAllActivities,
  )
  const displayCharacter = resolveEventParticipantCharacter(
    entry,
    applyToAllActivities ? undefined : activityId,
  )
  const isOwnCharacter =
    user?.id === entry.participant.characterReference.userId
  const canEditStatus = canAdminEdit || isOwnCharacter
  const classSpecializations = getSpecializationsByClass(
    displayCharacter.classId,
  )
  const spec = selection?.specId
    ? classSpecializations[selection.specId]
    : undefined
  const isSelected = variant === 'selected'
  const isBenched = variant === 'benched'
  const isUnavailable = variant === 'unavailable'
  const isStatusUnavailable = unavailableParticipantStatuses.includes(
    entry.participant.status,
  )
  const isMuted = isBenched || isUnavailable || isStatusUnavailable
  const canToggleSelection = canAdminEdit

  return (
    <div
      className={cn(
        'flex flex-col -mx-3 px-3',
        canToggleSelection && !isStatusUnavailable
          ? 'group/row cursor-pointer hover:bg-accent'
          : '',
      )}
      onClick={
        canToggleSelection && !isStatusUnavailable
          ? () =>
              updateSelection(
                entry.participant.id,
                { selected: !isSelected },
                applyToAllActivities ? undefined : activityId,
              )
          : undefined
      }
    >
      <div className='flex items-center justify-between w-full py-0.5'>
        <span className='flex items-center gap-2 min-w-0'>
          <span className='relative inline-flex'>
            {spec ? (
              <BlizzardIcon
                iconName={spec.icon}
                className={isMuted ? 'grayscale' : undefined}
              />
            ) : (
              <CharacterClassIcon
                characterClass={displayCharacter.characterClass}
                className={isMuted ? 'grayscale' : undefined}
              />
            )}
            <EventParticipantCommentIndicators
              comment={entry.participant.comment}
              adminComment={entry.participant.adminComment}
            />
          </span>
          <span
            className={
              isMuted
                ? 'truncate max-w-[9rem] text-muted-foreground opacity-70'
                : `truncate max-w-[9rem] ${
                    classColours[displayCharacter.characterClass].text
                  }`
            }
          >
            {displayCharacter.name}
          </span>
        </span>
        <span className={'flex items-center gap-0.5 ml-2'}>
          {canToggleSelection && !isStatusUnavailable ? (
            <ArrowUpDown className='h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover/row:opacity-100 mr-1' />
          ) : null}
          {hideStatus ? null : (
            <span onClick={(e) => e.stopPropagation()}>
              {canEditStatus ? (
                <DropdownMenu
                  open={statusDropdownOpen}
                  onOpenChange={setStatusDropdownOpen}
                  modal={false}
                >
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' size='icon' className='h-6 w-6'>
                      <EventStatusIcon
                        status={entry.participant.status}
                        size='small'
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className='w-fit p-1'
                    side='bottom'
                    align='center'
                  >
                    <EventParticipantStatusItems
                      currentStatus={entry.participant.status}
                      statuses={selectableStatuses}
                      onSelect={(status) => {
                        updateStatus(entry.participant.id, status)
                        setStatusDropdownOpen(false)
                      }}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-6 w-6 pointer-events-none'
                    >
                      <EventStatusIcon
                        status={entry.participant.status}
                        size='small'
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {capitalize(entry.participant.status)}
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
          )}
          <span className='group/ctx' onClick={(e) => e.stopPropagation()}>
            <EventParticipantContextMenu
              entry={entry}
              activityId={applyToAllActivities ? undefined : activityId}
              selectableStatuses={selectableStatuses}
              canEditStatus={canEditStatus}
              canAdminEdit={canAdminEdit}
              buttonVariant='outline'
              className="relative before:absolute before:content-[''] before:-top-2 before:-bottom-2 before:-right-2 before:left-0"
              hideEventActions={hideEventActions}
            />
          </span>
        </span>
      </div>
      {showPlanningInfo && !isUnavailable && !isStatusUnavailable ? (
        <div className='border-b border-border/70 px-1 pb-0.5'>
          <EventActivityParticipantActivityPlanningInfo
            entry={entry}
            activity={activity}
            contentOnly
            showSelectedCount={event.raidsPlanningScope === 'per_encounter'}
          />
        </div>
      ) : null}
    </div>
  )
}

export default EventActivitySetupParticipant
